import { BaseService } from './baseService';
import httpClient from './httpClient';
import { parseApiError } from '../utils/errorHandler';
import { normalizeCNPJ } from '../utils/masks';
import { isValidCNPJ } from '../utils/validation';

/**
 * Pessoa jurídica (clientes) — mesma API do seletor: GET /api/pessoa-juridica
 */
class LegalPersonService extends BaseService {
    constructor() {
        super('/api/pessoa-juridica');
    }

    /**
     * @param {Object} params
     * @param {number} [params.page]
     * @param {number} [params.size]
     * @param {string} [params.sort] - ex.: name,asc (único suportado no backend para esta listagem)
     * @param {string|null} [params.name]
     * @param {string|null} [params.document]
     * @returns {Promise<{ content: Array, page: Object }>}
     */
    async list({ page = 0, size = 10, sort = 'name,asc', name, document } = {}) {
        const filters = {};
        if (name) filters.name = name;
        if (document) filters.document = document;

        const data = await super.list({ page, size, sort, filters });
        const content = data._embedded?.content ?? data.content ?? [];
        const pageMeta = data.page ?? {
            totalPages: content.length === 0 ? 0 : 1,
            number: page,
            first: true,
            last: true,
        };

        return { content, page: pageMeta };
    }

    /**
     * Detalhe completo da pessoa jurídica (somente leitura na UI).
     * @param {string|number} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        return super.get(id);
    }

    /**
     * Dados cadastrais básicos por CNPJ (Receita / provedor).
     * @param {string} cnpj - Com ou sem máscara (path usa 14 caracteres A–Z/0–9)
     * @returns {Promise<Object>}
     */
    async getBasicDataByCnpj(cnpj) {
        const cleaned = normalizeCNPJ(cnpj);
        if (cleaned.length !== 14 || !isValidCNPJ(cleaned)) {
            const err = new Error('Informe um CNPJ válido para consultar.');
            err.status = 400;
            throw err;
        }
        const response = await httpClient.get(`${this.endpoint}/${cleaned}/dados-basicos`);
        if (!response.ok) {
            throw await parseApiError(response);
        }
        return response.json();
    }

    /**
     * Cria pessoa jurídica. Resposta 204 com header Location: /{id}
     * @param {Object} payload - CreateLegalPersonRequest (camelCase)
     * @returns {Promise<{ id: number|null }>}
     */
    async createLegalPerson(payload) {
        const response = await httpClient.post(this.endpoint, payload);
        if (!response.ok) {
            throw await parseApiError(response);
        }
        const location = response.headers.get('Location');
        let id = null;
        if (location) {
            const parts = String(location).split('/').filter(Boolean);
            const last = parts[parts.length - 1];
            if (/^\d+$/.test(last)) {
                id = Number(last, 10);
            }
        }
        return { id };
    }
}

const legalPersonService = new LegalPersonService();
export default legalPersonService;
